/**
 * Checklist Templates Management Page
 */

import { useState } from 'react';
import { Plus, Search, Edit, Trash2, Copy, MoreVertical, ClipboardList, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTemplates, useDeleteTemplate, useCreateTemplate } from '../hooks/useInstallationJobs';
import { TemplateEditorDialog } from '../components/TemplateEditorDialog';
import type { InstallationJobTemplate, ChecklistItemType } from '../types';

const ITEM_TYPE_LABELS: Record<ChecklistItemType, string> = {
  checkbox: 'Checkbox',
  text: 'Text',
  number: 'Number',
  date: 'Date',
  photo: 'Photo',
  signature: 'Signature',
  dropdown: 'Dropdown',
};

export default function ChecklistTemplatesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<InstallationJobTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<InstallationJobTemplate | null>(null);

  const { data, isLoading, isError, refetch } = useTemplates();
  const deleteTemplateMutation = useDeleteTemplate();
  const createTemplateMutation = useCreateTemplate();

  const templates = data?.templates ?? [];

  // Filter templates by search query
  const filteredTemplates = templates.filter((template) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      template.name.toLowerCase().includes(query) ||
      template.description?.toLowerCase().includes(query)
    );
  });

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setEditorOpen(true);
  };

  const handleEdit = (template: InstallationJobTemplate) => {
    setEditingTemplate(template);
    setEditorOpen(true);
  };

  const handleDuplicate = async (template: InstallationJobTemplate) => {
    try {
      await createTemplateMutation.mutateAsync({
        name: `${template.name} (Copy)`,
        description: template.description,
        checklistItems: template.checklistItems,
        toolItems: template.toolItems,
        materialItems: template.materialItems,
        termsAndConditions: template.termsAndConditions,
        defaultSensorFlags: template.defaultSensorFlags,
        isActive: template.isActive,
      });
      toast.success('Template duplicated successfully');
    } catch {
      toast.error('Failed to duplicate template');
    }
  };

  const handleDeleteClick = (template: InstallationJobTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    try {
      await deleteTemplateMutation.mutateAsync(templateToDelete.id);
      toast.success('Template deleted successfully');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingTemplate(null);
  };

  const getItemTypeSummary = (template: InstallationJobTemplate) => {
    const counts: Partial<Record<ChecklistItemType, number>> = {};
    template.checklistItems.forEach((item) => {
      counts[item.type] = (counts[item.type] || 0) + 1;
    });
    return counts;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Checklist Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Create and manage reusable checklist templates for installation jobs
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button variant="outline" size="icon" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Templates Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-60 mt-2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : isError ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground mb-4">Failed to load templates</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            {searchQuery ? (
              <>
                <p className="text-muted-foreground">No templates match your search</p>
                <Button variant="link" onClick={() => setSearchQuery('')}>
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-4">No templates yet</p>
                <Button onClick={handleCreateNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Template
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredTemplates.map((template) => {
            const itemCounts = getItemTypeSummary(template);
            const totalItems = template.checklistItems.length;
            const requiredItems = template.checklistItems.filter((i) => i.required).length;

            return (
              <Card key={template.id} className="relative group">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 pr-8">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {template.name}
                        {!template.isActive && (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </CardTitle>
                      {template.description && (
                        <CardDescription className="line-clamp-2">
                          {template.description}
                        </CardDescription>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 absolute top-4 right-4"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(template)}>
                          <Edit className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => handleDeleteClick(template)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Summary Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        <strong className="text-foreground">{totalItems}</strong> items
                      </span>
                      <span className="text-muted-foreground">
                        <strong className="text-foreground">{requiredItems}</strong> required
                      </span>
                    </div>

                    {/* Item Type Badges */}
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(itemCounts).map(([type, count]) => (
                        <Badge key={type} variant="outline" className="text-xs">
                          {count} {ITEM_TYPE_LABELS[type as ChecklistItemType]}
                        </Badge>
                      ))}
                    </div>

                    {/* Sensor Flags */}
                    {template.defaultSensorFlags.length > 0 && (
                      <div className="pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-1">Default Sensor Flags:</p>
                        <div className="flex flex-wrap gap-1">
                          {template.defaultSensorFlags.slice(0, 3).map((flag) => (
                            <Badge key={flag} variant="secondary" className="text-xs">
                              {flag}
                            </Badge>
                          ))}
                          {template.defaultSensorFlags.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{template.defaultSensorFlags.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Template Editor Dialog */}
      <TemplateEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        template={editingTemplate}
        onClose={handleEditorClose}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
